import {
  TRACK_ENDPOINT,
  AnalyticsEventType,
} from './analyticsConfig';

/**
 * Privacy-conscious client analytics.
 *
 * - Reporting is OPT-IN. Nothing is sent unless the user has consented.
 * - We send only an aggregate event name (and template id for template
 *   events) plus a rotating, per-day anonymous "visitor" hash used purely
 *   to estimate active users. The hash changes every day and is not linkable
 *   across days, and it contains no PII.
 */

const CONSENT_KEY = 'bch-signature:analytics-consent';
const SALT_KEY = 'bch-signature:anon-salt';

export function hasAnalyticsConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'granted';
  } catch {
    return false;
  }
}

export function setAnalyticsConsent(granted: boolean): void {
  try {
    localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied');
  } catch {
    // ignore
  }
}

export function hasAnsweredConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) !== null;
  } catch {
    return false;
  }
}

/** A stable-but-anonymous random salt for this browser install. */
function getSalt(): string {
  try {
    let salt = localStorage.getItem(SALT_KEY);
    if (!salt) {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      salt = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      localStorage.setItem(SALT_KEY, salt);
    }
    return salt;
  } catch {
    return 'anon';
  }
}

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('');
}

/**
 * A rotating per-day visitor hash: sha256(salt + today). Because it includes
 * the date, it cannot be correlated across days.
 */
async function visitorHash(): Promise<string> {
  const today = new Date().toISOString().slice(0, 10);
  const full = await sha256Hex(`${getSalt()}:${today}`);
  return full.slice(0, 32);
}

interface TrackPayload {
  event: AnalyticsEventType;
  template?: string;
  visitor?: string;
}

/** Fire-and-forget beacon. Respects consent; never throws. */
export async function track(
  event: AnalyticsEventType,
  opts: { template?: string } = {},
): Promise<void> {
  if (!hasAnalyticsConsent()) return;
  try {
    const payload: TrackPayload = {
      event,
      visitor: await visitorHash(),
    };
    if (opts.template) payload.template = opts.template;

    const body = JSON.stringify(payload);

    // Prefer sendBeacon so it survives navigation; fall back to fetch.
    if (typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(TRACK_ENDPOINT, blob);
      if (ok) return;
    }
    await fetch(TRACK_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    // Analytics must never break the app.
  }
}
